import React, { useState } from 'react';
import { supabase } from '../../shared/services/supabase';
import { Input } from '../../shared/components/ui/Input';
import { Label } from '../../shared/components/ui/Label';
import { useToast } from '../../shared/hooks/use-toast';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { cn } from '../../shared/utils/cn';
import { analytics } from '../../shared/utils/analytics';
import { SocialProof } from './SocialProof';
import { RegistrationsClosedNotice } from './RegistrationsClosedNotice';
import type { PortalRole } from '../authMetadata';
import { PENDING_PORTAL_ROLE_STORAGE_KEY } from '../authMetadata';

interface AuthFormSectionProps {
  idPrefix?: string;
  /**
   * Drives the role-aware "registrations closed" link and the OAuth pending-role
   * hint. New signups are disabled (CollabsMap transition); this surface is
   * sign-in only.
   */
  portalRole?: PortalRole;
}

export const AuthFormSection: React.FC<AuthFormSectionProps> = ({
  idPrefix = '',
  portalRole = 'creator',
}) => {
  const getFieldId = (field: string) => idPrefix ? `${idPrefix}-${field}` : field;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailForm] = useState(true);
  const { toast } = useToast();

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Sign-in only — new account creation is disabled on the legacy portal.
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;

      // Track login event
      analytics.login('email');
    } catch (error) {
      console.error('Auth error:', error);

      // Handle specific Supabase auth errors
      if (error instanceof Error) {
        if (error.message?.includes('Invalid login credentials')) {
          toast({
            title: 'Invalid credentials',
            description: 'Please check your email and password.',
            variant: 'destructive',
          });
        } else if (error.message?.includes('Email not confirmed')) {
          toast({
            title: 'Email not confirmed',
            description: 'Please check your email for the confirmation link.',
            variant: 'destructive',
          });
        } else if ('code' in error && error.code === 'PGRST301') {
          toast({
            title: 'Invalid credentials',
            description: 'Please check your email and password.',
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Authentication failed',
            description: error.message,
            variant: 'destructive',
          });
        }
      } else {
        toast({
          title: 'Authentication failed',
          description: 'Please try again.',
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    try {
      try {
        sessionStorage.setItem(PENDING_PORTAL_ROLE_STORAGE_KEY, portalRole);
      } catch {
        // ignore storage failures (private mode, etc.)
      }
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
    } catch (error) {
      console.error('Google auth error:', error);
      toast({
        title: 'Google authentication failed',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const isMobile = idPrefix === 'mobile';
  
  return (
    <div className={cn(
      "w-full mx-auto",
      isMobile ? "p-0" : "max-w-lg p-4 lg:p-8"
    )}>
      {/* Social auth + divider */}
      <div className={cn("space-y-4", isMobile ? "mb-6" : "mb-4")}>
        <button
          type="button"
          className={cn(
            "w-full h-12 rounded-lg border border-gray-300 dark:border-gray-600",
            "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100",
            "font-satoshi font-medium text-sm",
            "flex items-center justify-center gap-3",
            "hover:bg-gray-50 dark:hover:bg-gray-700",
            "transition-colors duration-200",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
          onClick={handleGoogleAuth}
          disabled={loading}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Continue with Google
        </button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-gray-200 dark:border-gray-700" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className={cn(
              "px-3 text-muted-foreground font-satoshi text-sm",
              isMobile 
                ? "bg-gray-50 dark:bg-gray-900" // Mobile has gradient from-gray-50
                : "bg-white dark:bg-gray-900"    // Desktop right column is white
            )}>
              or continue with email
            </span>
          </div>
        </div>
      </div>

      <div className={cn(
        "rounded-xl border border-gray-200 dark:border-gray-700",
        "bg-white dark:bg-gray-900",
        isMobile ? "p-5 shadow-sm" : "p-6"
      )}>
        {/* Header removed per spec */}

        {/* Email Form */}
        {showEmailForm && (
          <>
          {/* Email/Password Form */}
          <form onSubmit={handleEmailAuth} className={cn(isMobile ? "space-y-5" : "space-y-4")}>
            <div className="space-y-2">
              <Label htmlFor={getFieldId('email')} className={cn(
                "font-satoshi font-medium text-sm text-gray-700 dark:text-gray-300"
              )}>
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id={getFieldId('email')}
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor={getFieldId('password')} className={cn(
                "font-satoshi font-medium text-sm text-gray-700 dark:text-gray-300"
              )}>
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id={getFieldId('password')}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  disabled={loading}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className={cn(
                "w-full h-12 rounded-lg",
                "bg-[var(--color-primary-orange)] hover:opacity-90",
                "text-white font-satoshi font-bold text-sm uppercase",
                "transition-all duration-200",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                isMobile ? "mt-4" : "mt-3"
              )}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing in...
                </div>
              ) : (
                'Sign in'
              )}
            </button>
          </form>
          </>
        )}
      </div>

      {/* New account creation is disabled (CollabsMap transition). The signup
          toggle is replaced by a role-aware "registrations closed" note. */}
      <div className={cn(isMobile ? "mt-5" : "mt-4")}>
        <RegistrationsClosedNotice role={portalRole} />
      </div>

      {/* Social Proof - Mobile only, below form and mode toggle */}
      {isMobile && (
        <div className="mt-6">
          <SocialProof />
        </div>
      )}
    </div>
  );
};
